import { Button, Card, Modal, showNotification } from 'design-system';
import { Typography } from 'design-system/compat';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { request } from '../../../service/request';
import styles from './CreateMembership.module.css';
import GroupButtons from './GroupButtons';

const { Title, Text } = Typography;

const SuccessMessage = ({
  userId,
  name,
  email,
  confirmationEmailStatus,
  confirmationEmailErrorCode,
  confirmationEmailErrorMessage,
  setHidePage,
  isJoinSheet = false,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [isResending, setIsResending] = useState(false);
  const [displayStatus, setDisplayStatus] = useState(confirmationEmailStatus);
  const [displayErrorCode, setDisplayErrorCode] = useState(confirmationEmailErrorCode);
  const [displayErrorMessage, setDisplayErrorMessage] = useState(confirmationEmailErrorMessage);
  const [pollVersion, setPollVersion] = useState(0);

  useEffect(() => {
    setDisplayStatus(confirmationEmailStatus);
    setDisplayErrorCode(confirmationEmailErrorCode);
    setDisplayErrorMessage(confirmationEmailErrorMessage);
  }, [confirmationEmailErrorCode, confirmationEmailErrorMessage, confirmationEmailStatus]);

  useEffect(() => {
    if (displayStatus !== 'pending' && displayStatus !== 'sending') return undefined;
    const timer = window.setTimeout(() => {
      setDisplayStatus((prev) => (prev === 'pending' || prev === 'sending' ? 'sent' : prev));
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [displayStatus]);

  useEffect(() => {
    const shouldPoll = ['pending', 'sending', 'sent'].includes(displayStatus);
    if (!shouldPoll || (!userId && !email)) return undefined;

    let cancelled = false;
    let elapsedMs = 0;
    const intervalMs = 2000;
    const maxMs = 20000;

    const pollStatus = async () => {
      try {
        const res = await request(
          '/account_confirmations/status',
          {user: {user_id: userId, email}},
          'post',
        );
        if (cancelled) return;

        const status = res?.data?.confirmation_email_status;
        const errorCode = res?.data?.confirmation_email_error_code || null;
        const errorMessage = res?.data?.confirmation_email_error_message || null;

        if (status) {
          setDisplayStatus(status);
          setDisplayErrorCode(errorCode);
          setDisplayErrorMessage(errorMessage);
        }
      } catch {
      } finally {
        elapsedMs += intervalMs;
      }
    };

    const timer = window.setInterval(() => {
      if (cancelled || elapsedMs >= maxMs) return;
      pollStatus();
    }, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [displayStatus, email, pollVersion, userId]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const navigateToLogin = () => navigate('/sign-in');

  const handleResend = () => {
    if (!email || isResending) return;
    setIsResending(true);
    const payload = {
      user: {
        email,
      },
    };
    request(`/account_confirmations/resend_account_confirmation_email`, payload, 'post')
      .then(() => {
        setDisplayStatus('sent');
        setDisplayErrorCode(null);
        setDisplayErrorMessage(null);
        setPollVersion((v) => v + 1);
        showNotification(intl.formatMessage({ id: 'signup_resend_toast' }), 'success', 'auth');
      })
      .catch((err) => {
        const message = err.response?.data?.error || err.message;
        showNotification(message, 'error', 'auth');
      })
      .finally(() => setIsResending(false));
  };

  const resolveDeliveryMessage = () => {
    if (displayStatus === 'pending') {
      return intl.formatMessage({ id: 'signup_delivery_status_sending' });
    }
    if (displayStatus === 'delivered') {
      return intl.formatMessage({ id: 'signup_delivery_status_delivered' });
    }
    if (displayStatus === 'temporary_failure') {
      if (displayErrorCode === 'over_quota') {
        return intl.formatMessage({ id: 'signup_delivery_status_temp_over_quota' });
      }
      return (
        displayErrorMessage ||
        intl.formatMessage({ id: 'signup_delivery_status_temporary_failure' })
      );
    }
    if (displayStatus === 'permanent_failure') {
      return (
        displayErrorMessage ||
        intl.formatMessage({ id: 'signup_delivery_status_permanent_failure' })
      );
    }
    if (displayStatus === 'sending') {
      return intl.formatMessage({ id: 'signup_delivery_status_sending' });
    }
    if (displayStatus === 'sent') {
      return intl.formatMessage({ id: 'signup_delivery_status_sent' });
    }
    return null;
  };

  const deliveryMessage = resolveDeliveryMessage();

  const emailValue = email ? (
    <span className={styles.successEmail}>{email}</span>
  ) : (
    <span className={styles.successEmail}>—</span>
  );

  const content = (
    <div className={styles.successContent}>
      {!isJoinSheet && (
        <Title level={2} className={styles.successHeading}>
          <FormattedMessage id="signup_message_heading" />
        </Title>
      )}
      <Text className={styles.successSubheading}>
        <FormattedMessage id="signup_message_subheading" values={{ name }} />
      </Text>
      <div className={styles.successHighlight}>
        <FormattedMessage id="signup_primary_cta" values={{ email: emailValue }} />
      </div>
      <Text className={styles.successMessage}>
        <FormattedMessage id="signup_secondary_cta" />
      </Text>
      <Text className={styles.successMessage}>
        <FormattedMessage id="signup_help_text" />
      </Text>
      {deliveryMessage && (
        <Text className={styles.successMessage}>
          <FormattedMessage id="signup_delivery_status_label" /> {deliveryMessage}
        </Text>
      )}
      <div className={styles.successActions}>
        <Button
          type="primary"
          shape="round"
          onClick={handleResend}
          loading={isResending}
          disabled={!email}
        >
          <FormattedMessage id="resend_email" />
        </Button>
      </div>
      {!isJoinSheet && (
        <GroupButtons
          backKey="signup_change_email"
          nextKey="signup_login_action"
          onBack={() => setHidePage(false)}
          onNext={navigateToLogin}
        />
      )}
    </div>
  );

  if (isJoinSheet) {
    return (
      <Modal
        open
        onClose={() => setHidePage(false)}
        title={<FormattedMessage id="signup_message_heading" />}
        onOk={navigateToLogin}
        okText={<FormattedMessage id="signup_login_action" />}
        cancelText={<FormattedMessage id="signup_change_email" />}
        centered
      >
        {content}
      </Modal>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content} style={{ minHeight: 'calc(100vh - 200px)' }}>
        <Card className={styles.card}>
          <div className={styles.cardContent}>
            {content}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SuccessMessage;
